import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/core/auth';
import { LoginPage, SecurityPage } from '@/features/auth';
import { DashboardPage } from '@/features/dashboard';
import { CompaniesPage, CompanyDetailsLayout } from '@/features/companies';
import { CompanyDetailsOverviewTab } from '@/features/companies/company-details-tabs/CompanyDetailsOverviewTab';
import { CompanyDetailsSubscriptionTab } from '@/features/companies/company-details-tabs/CompanyDetailsSubscriptionTab';
import { CompanyDetailsMembersTab } from '@/features/companies/company-details-tabs/CompanyDetailsMembersTab';
import { CompanyDetailsIntegrationsTab } from '@/features/companies/company-details-tabs/CompanyDetailsIntegrationsTab';
import { CompanyDetailsPh24Tab } from '@/features/companies/company-details-tabs/CompanyDetailsPh24Tab';
import { InvoicesPage } from '@/features/invoices';
import { CompanyDetailsConnectorTab } from '@/features/companies/company-details-tabs/CompanyDetailsConnectorTab';
import { PlansPage } from '@/features/plans';
import { ProductsPage } from '@/features/products';
import { UsersPage } from '@/features/users';
import { ModulesPage } from '@/features/modules';
import { CouponsPage } from '@/features/coupons';
import { AffiliatesPage } from '@/features/affiliates';
import { SettingsPage, SettingsEntryRedirect } from '@/features/settings';
import { StaffAccessLogsPage } from '@/features/staff-access-logs';
import { UsagePage } from '@/features/usage';
import { ConnectorGatewaysPage, SidecarReleasesPage } from '@/features/connector-gateways';
import { ConversationMigrationPage } from '@/features/migration';
import { CompanyDeletionsPage } from '@/features/company-deletions';
import { AnnouncementsPage } from '@/features/announcements';

function RedirectWithId({ to }: { to: (id: string) => string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? to(id) : '/companies'} replace />;
}

function CompanyDetailsUnknownSegmentRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/companies" replace />;
  return <Navigate to={`/companies/${id}/overview`} replace />;
}

/** SUPPORT só acessa o fluxo "acessar empresa" (lista/detalhe) e a própria segurança. */
const SUPPORT_ALLOWED_PREFIXES = ['/companies', '/security'];

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (
    user?.role === 'SUPPORT' &&
    !SUPPORT_ALLOWED_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))
  ) {
    return <Navigate to="/companies" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <SecurityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <CompaniesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedRoute>
            <CompanyDetailsLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<CompanyDetailsOverviewTab />} />
        <Route path="plans" element={<CompanyDetailsSubscriptionTab />} />
        <Route path="subscription" element={<Navigate to="../plans" replace />} />
        <Route path="usage" element={<Navigate to="../plans" replace />} />
        <Route path="billing" element={<Navigate to="../plans" replace />} />
        <Route path="modules" element={<Navigate to="../plans" replace />} />
        <Route path="members" element={<CompanyDetailsMembersTab />} />
        <Route path="integrations" element={<CompanyDetailsIntegrationsTab />} />
        <Route path="ph24" element={<CompanyDetailsPh24Tab />} />
        <Route path="connector" element={<CompanyDetailsConnectorTab />} />
        <Route path="*" element={<CompanyDetailsUnknownSegmentRedirect />} />
      </Route>
      <Route
        path="/company-deletions"
        element={
          <ProtectedRoute>
            <CompanyDeletionsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/empresas" element={<Navigate to="/companies" replace />} />
      <Route path="/empresas/:id" element={<RedirectWithId to={(id) => `/companies/${id}/overview`} />} />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <PlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coupons"
        element={
          <ProtectedRoute>
            <CouponsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliates"
        element={
          <ProtectedRoute>
            <AffiliatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules"
        element={
          <ProtectedRoute>
            <ModulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/migration"
        element={
          <ProtectedRoute>
            <ConversationMigrationPage />
          </ProtectedRoute>
        }
      />
      <Route path="/migracao" element={<Navigate to="/migration" replace />} />
      <Route path="/assinaturas" element={<Navigate to="/subscriptions" replace />} />
      <Route path="/subscriptions" element={<Navigate to="/invoices" replace />} />
      <Route path="/faturas" element={<Navigate to="/invoices" replace />} />
      <Route path="/planos" element={<Navigate to="/plans" replace />} />
      <Route path="/cupons" element={<Navigate to="/coupons" replace />} />
      <Route path="/afiliados" element={<Navigate to="/affiliates" replace />} />
      <Route path="/produtos" element={<Navigate to="/products" replace />} />
      <Route path="/usuarios" element={<Navigate to="/users" replace />} />
      <Route path="/modulos" element={<Navigate to="/modules" replace />} />
      <Route path="/servidores-whatsapp" element={<Navigate to="/settings/servidores-uazapi" replace />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsEntryRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/:sectionId"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/configuracoes" element={<Navigate to="/settings/gateways" replace />} />
      <Route path="/configuracoes/gateways" element={<Navigate to="/settings/gateways" replace />} />
      <Route path="/configuracoes/meta" element={<Navigate to="/settings/meta" replace />} />
      <Route path="/configuracoes/servidores-uazapi" element={<Navigate to="/settings/servidores-uazapi" replace />} />
      <Route path="/configuracoes/branding" element={<Navigate to="/settings/branding" replace />} />
      <Route path="/configuracoes/asaas" element={<Navigate to="/settings/gateways" replace />} />
      <Route
        path="/connector-gateways"
        element={
          <ProtectedRoute>
            <ConnectorGatewaysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sidecar-releases"
        element={
          <ProtectedRoute>
            <SidecarReleasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/staff-access-logs"
        element={
          <ProtectedRoute>
            <StaffAccessLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usage"
        element={
          <ProtectedRoute>
            <UsagePage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
